import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useModalStore } from '@/stores/modal'
import { useAIStore } from '@/stores/openai'
import { useUpgradeStore } from '@/stores/upgrade'
import { useUserStore } from '@/stores/user'
import { http } from '@/helpers/http'
import { Products } from '@/helpers/product'
import { formatDecimalNumber } from '@/helpers/styles.ts'
import { findNearestAmazonCoverSize } from '@/helpers/pageSize.ts'
import { getSpineData, minimumPageCountForUsingTextOnSpine } from '@/helpers/templatePreviewHelperForCovers.ts'
import { track } from '@/helpers/mixpanelDes'
import { coverKeywordsText, normalizeCoverContent } from '@/helpers/coverElements'
import type {
  TAmazonBindingType,
  TAmazonElement,
  TAmazonCover,
  TAmazonCoverDimensionsForm,
  TAmazonCoversResult,
  TAmazonCoverThumbnailInfo,
  TAmazonPageSetup,
  TAmazonThumbnailInfo,
  TAmazonElementText,
  TAmazonTextElementType,
  TAmazonCoverSection,
  TAmazonCoverThumbnailResult,
} from '@/types/amazon-cover'

import type { TProject } from '@/types/projects'
import type { TDropdownOption } from '@/types/dropdown'
import { useRoute } from 'vue-router'
import type { TAmazonCoverSize } from '@/types/cover.ts'

export const useAmazonCoversStore = defineStore('amazon-covers', () => {
  const route = useRoute()
  const covers = ref<TAmazonCover[]>([])
  const cover = ref<TAmazonCover | null>(null)
  const modal = useModalStore()
  const openAI = useAIStore()

  const upgrade = ['cover-editor-preview', 'print-book-pdf'].some((r) => route.name?.toString().includes(r))
    ? null
    : useUpgradeStore()
  const users = useUserStore()
  const listSortBy = ref<string>('newest')
  const listPage = ref<number>(1)
  const total = ref<number>(0)
  const minPageAmountForHardcover = 75
  const minPageAmountForPaperback = 24

  const idsWithoutThumbnails = ref<number[]>([])
  const generateRunning = ref<boolean>(false)
  const generateTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
  const loaded = ref<boolean>(false)
  let pendingLoad: Promise<void> | null = null

  function invalidate(): void {
    loaded.value = false
  }

  const barcodeZIndex = computed((): number => {
    const maxZIndex = Math.max(...(cover.value?.content.map((el) => el.zIndex ?? 0) ?? [0]))
    return maxZIndex + 1
  })

  const bindingTypeOption: TDropdownOption[] = [
    { name: 'kindle', label: 'Kindle', icon: 'fa-light fa-tablet' },
    { name: 'paperback', label: 'Paperback', icon: 'fa-light fa-book-open' },
    { name: 'hardcover', label: 'Hardcover', icon: 'fa-light fa-book' },
  ]

  const interiorTypeOptions: TDropdownOption[] = [
    { name: 'black_and_white', label: 'Black and white' },
    { name: 'premium_color', label: 'Premium color' },
    { name: 'standard_color', label: 'Standard color' },
  ]

  const paperTypeOptions: TDropdownOption[] = [
    { name: 'cream_paper', label: 'Cream paper' },
    { name: 'white_paper', label: 'White paper' },
  ]

  const combinedPaperTypeOptions: TDropdownOption[] = [
    { name: 'black_and_white__white_paper', label: 'Black ink, white paper' },
    { name: 'black_and_white__cream_paper', label: 'Black ink, cream paper' },
    { name: 'premium_color__white_paper', label: 'Premium color, white paper' },
    { name: 'standard_color__white_paper', label: 'Standard color, white paper' },
  ]

  const images: Record<TAmazonBindingType, string> = {
    hardcover: '/dashboard/modals/hardcover.png',
    paperback: '/dashboard/modals/paperback.png',
    kindle: '/dashboard/modals/kindle.png',
  }

  function collectIdsWithoutThumbnails(): void {
    idsWithoutThumbnails.value = covers.value.filter((item) => !item.thumbnail).map((item) => item.id)
  }

  function startThumbnailGeneration(): void {
    generateRunning.value = true
    if (generateTimeout.value) {
      clearTimeout(generateTimeout.value)
    }
    checkThumbnail()
  }

  async function checkThumbnail(): Promise<void> {
    const id = idsWithoutThumbnails.value.length > 0 ? idsWithoutThumbnails.value[0] : null
    if (!id) {
      generateRunning.value = false
      return
    }

    if (idsWithoutThumbnails.value.length > 0) {
      idsWithoutThumbnails.value.shift()
    }

    if (generateRunning.value === true) {
      await generateThumbnailById(id)
      generateTimeout.value = setTimeout(() => {
        checkThumbnail()
      }, 1000)
    } else {
      generateRunning.value = false
    }
  }

  function sort(sortBy: string) {
    switch (sortBy) {
      case 'az':
        covers.value.sort((a, b) => a.title.localeCompare(b.title))
        break
      case 'za':
        covers.value.sort((a, b) => b.title.localeCompare(a.title))
        break
      case 'oldest':
        covers.value.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
        break
      case 'newest':
        covers.value.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        break
    }
  }

  /**
   * Apply the versioned URLs a regeneration returned, so the new image is
   * requested under a new key while an untouched one keeps its cache entry.
   */
  function applyGeneratedThumbnail(id: number, res: TAmazonCoverThumbnailResult): void {
    const rows = [covers.value.find((x) => x.id === id), cover.value].filter(
      (row): row is TAmazonCover => row?.id === id,
    )

    for (const row of rows) {
      row.thumbnail = true
      if (res.image_url) {
        row.image_url = res.image_url
      }
      if (res.thumb_url) {
        row.thumb_url = res.thumb_url
      }
    }
  }

  async function generateThumbnailById(id: number): Promise<void> {
    try {
      const res = await http.get<TAmazonCoverThumbnailResult>(`/amazon-covers/generate-thumbnail/${id}`)
      if (res?.result) {
        applyGeneratedThumbnail(id, res)
      }
    } catch {
      console.log('Failed to generate thumbnail')
    }
  }

  async function fetch(nextPage: boolean = false, sortBy: string = '', query: string = ''): Promise<void> {
    if (nextPage === false) {
      listPage.value = 1
    }
    if (sortBy !== '') {
      listSortBy.value = sortBy
    }

    try {
      const suffix = query ? `?query=${encodeURIComponent(query)}` : ''
      const data = await http.get<TAmazonCoversResult>('/amazon-covers/' + listPage.value + '/' + listSortBy.value + suffix)
      if (nextPage === false) {
        covers.value = data.results
      } else {
        covers.value = covers.value.concat(data.results)
      }
      total.value = data.pagination.total
      loaded.value = query === ''

      collectIdsWithoutThumbnails()

      if (nextPage === false) {
        startThumbnailGeneration()
      }
    } catch {
      console.log('Failed to fetch covers')
    }
    return
  }

  async function ensureCovers(): Promise<void> {
    if (loaded.value) {
      collectIdsWithoutThumbnails()
      startThumbnailGeneration()
      return
    }
    if (pendingLoad) {
      return pendingLoad
    }

    pendingLoad = fetch()
    try {
      await pendingLoad
    } finally {
      pendingLoad = null
    }
  }

  async function fetchNext(): Promise<void> {
    listPage.value++
    await fetch(true)
    if (generateTimeout.value) {
      clearTimeout(generateTimeout.value)
    }
    checkThumbnail()
  }

  async function fetchBackgroundThumbnailInfo(): Promise<void> {
    if (!cover.value?.id) {
      return
    }

    try {
      const data = await http.get<{ results: TAmazonThumbnailInfo[] }>(
        '/amazon-covers/get-background-thumbnail-info/' + cover.value.id,
      )
      if (data && cover?.value) {
        cover.value.background_thumbnails = data.results
      } else {
        console.log('Failed to fetch covers')
      }
    } catch {
      console.log('Failed to fetch covers')
    }
    return
  }

  async function get(id: number, token?: string): Promise<TAmazonCover | null> {
    cover.value = null
    try {
      const loaded = await http.get<TAmazonCover>('/amazon-covers/cover/' + id + (token ? '/' + token : ''))

      cover.value = { ...loaded, content: normalizeCoverContent(loaded.content ?? []) }
    } catch {
      console.log('Failed to fetch cover')
    }

    return cover.value
  }

  async function generateCoverKeywords(): Promise<{ iconFinder: string; unsplash: string }> {
    const keywords = cover.value ? await openAI.getSearchKeywords(coverKeywordsText(cover.value)) : null

    return { iconFinder: keywords?.icons[0] ?? keywords?.keywords[0] ?? '', unsplash: keywords?.query ?? '' }
  }

  async function changeCoverTemplate(coverId: number, templateId: number): Promise<boolean> {
    try {
      const res = await http.post<{ result: boolean }>('/amazon-covers/change-template', {
        cover_id: coverId,
        template_id: templateId,
      })
      return res.result
    } catch {
      console.log('Failed to change template')
      return false
    }
  }

  async function save(cover: TAmazonCover, skipMixPanelLog?: boolean): Promise<number | null> {
    if (!Array.isArray(cover.content) || cover.content.length === 0) {
      const error = new Error('DES-4306 Attempted to save cover with invalid content')
      window?.Bugsnag?.notify(error, (event: { addMetadata: (key: string, data: object) => void }) => {
        event.addMetadata('coverDebug', {
          coverId: cover.id,
          contentType: typeof cover.content,
          contentValue: cover.content,
          isArray: Array.isArray(cover.content),
          contentLength: Array.isArray(cover.content) ? cover.content.length : null,
          templateId: cover.template_id,
          projectId: cover.project_id,
          bindingType: cover.binding,
          title: cover.title,
        })
      })
      console.error('DES-4306: cover content is invalid', {
        coverId: cover.id,
        contentType: typeof cover.content,
        contentValue: cover.content,
      })
      return null
    }

    try {
      const res = await http.post<{ result: boolean; id: number }>('/amazon-covers/cover', cover)
      if (!skipMixPanelLog) {
        track('cover-edit-save', { cover_id: res.id })
      }
      if (res.result) {
        invalidate()
      }

      if (res.result && cover.thumbnail === false) {
        if (!idsWithoutThumbnails.value.includes(res.id)) {
          idsWithoutThumbnails.value.push(res.id)
        }
        if (!generateRunning.value) {
          generateRunning.value = true
          if (generateTimeout.value) {
            clearTimeout(generateTimeout.value)
          }
          checkThumbnail()
        }
      }

      return res.result ? res.id : null
    } catch {
      console.log('Failed to save cover')
      return null
    }
  }

  async function generateThumbnail(cover: TAmazonCover) {
    try {
      const res = await http.get<TAmazonCoverThumbnailResult>(`/amazon-covers/generate-thumbnail/${cover.id}`)
      applyGeneratedThumbnail(cover.id, res)
    } catch {
      console.log('Failed to save cover')
      return null
    }
  }

  async function remove(id: number): Promise<boolean> {
    try {
      const idx = idsWithoutThumbnails.value.indexOf(id)
      if (idx >= 0) {
        idsWithoutThumbnails.value.splice(idx, 1)
      }

      const res = await http.delete<{ result: boolean }>('/amazon-covers/cover/' + id)
      res.result && fetch()
      return res.result || false
    } catch {
      console.log('Failed to remove cover')
    }
    return false
  }

  function isSpineText(el: TAmazonElement): el is TAmazonElementText {
    const spineTextIds = ['spine-title', 'spine-author']
    const spineTextTypes: TAmazonTextElementType[] = ['title', 'author']

    return spineTextIds.includes(String(el.id)) && spineTextTypes.includes(el.type as TAmazonTextElementType)
  }

  function adjustSpineAuthorTop(spineTextElements: TAmazonElementText[], newSpineData: TAmazonElement[]) {
    const spineBackgroundElement = newSpineData.find((el) => el.id === 'spine-background')
    if (!spineBackgroundElement || !cover.value?.page_setup?.spine?.height_background) {
      return
    }

    spineTextElements.forEach((spineTextElement: TAmazonElementText) => {
      if (spineTextElement.id === 'spine-author' && cover.value?.page_setup?.spine?.height_background) {
        const spineAuthorTopAdjustment =
          cover.value.page_setup.spine.height_background - (spineBackgroundElement.height ?? 0)
        spineTextElement.top = formatDecimalNumber((spineTextElement.top ?? 0) + spineAuthorTopAdjustment)
      }
    })
  }

  async function handleSpineTitleAndAuthorAfterPageCountChange(newPagesCount: number) {
    const hasSpineText = cover.value?.content?.some(isSpineText)

    if (newPagesCount < minimumPageCountForUsingTextOnSpine && hasSpineText && cover.value?.content) {
      // remove spine text elements if needed
      cover.value.content = cover.value.content.filter((el) => !isSpineText(el))
    } else if (newPagesCount >= minimumPageCountForUsingTextOnSpine && cover.value) {
      const newSpineData = await getSpineData(cover.value.page_setup, newPagesCount, cover.value.content, [])
      const spineTextElements: TAmazonElementText[] = newSpineData.filter(isSpineText)
      adjustSpineAuthorTop(spineTextElements, newSpineData)

      if (!hasSpineText) {
        // create and add spine text elements if needed
        cover.value.content.push(...spineTextElements)
      } else {
        // adjust spine text elements if they actually exist
        updateSpineTextElements(newPagesCount, spineTextElements)
      }
    }
  }

  function updateSpineTextElements(newPagesCount: number, spineTextElements: TAmazonElementText[]) {
    if (!cover.value) return

    const existingSpineTitle = cover.value.content.find(
      (el): el is TAmazonElementText => isSpineText(el) && el.type === 'title',
    )

    const existingSpineAuthor = cover.value.content.find(
      (el): el is TAmazonElementText => isSpineText(el) && el.type === 'author',
    )

    const newSpineTitle = spineTextElements.find((el) => String(el.id) === 'spine-title')
    const newSpineAuthor = spineTextElements.find((el) => String(el.id) === 'spine-author')

    const updatedSpineElements: TAmazonElement[] = []

    if (newSpineTitle && existingSpineTitle) {
      newSpineTitle.color = existingSpineTitle.color
      newSpineTitle.backgroundColor = existingSpineTitle.backgroundColor
    }
    if (newSpineTitle) updatedSpineElements.push(newSpineTitle)

    if (newSpineAuthor && existingSpineAuthor) {
      newSpineAuthor.color = existingSpineAuthor.color
      newSpineAuthor.backgroundColor = existingSpineAuthor.backgroundColor
    }
    if (newSpineAuthor) updatedSpineElements.push(newSpineAuthor)

    // replace old spine elements with updated ones
    cover.value.content = cover.value.content.filter((el) => !isSpineText(el))
    cover.value.content.push(...updatedSpineElements)
  }

  async function adjustCoverToTheNewPageCount(id: number, newPageCount: number, selectedDimensions: TAmazonCoverSize) {
    const cover: TAmazonCover | null = await get(id)
    if (!cover?.id || !selectedDimensions.width || !selectedDimensions.height) {
      return
    }

    const newFormData: TAmazonCoverDimensionsForm = {
      binding_type: cover.binding,
      interior_type: cover.interior,
      paper_type: cover.paper,
      reading_direction: 'ltr',
      interior_trim_width: selectedDimensions.width,
      interior_trim_height: selectedDimensions.height,
      page_count: newPageCount,
    }

    const newDimensions: TAmazonPageSetup | null = await getDimensions(newFormData)
    if (!newDimensions) {
      return
    }

    const yDifference = newDimensions.back.y - cover.page_setup.back.y
    const backXDifference = newDimensions.back.x - cover.page_setup.back.x
    const spineXDifference = newDimensions.spine.x - cover.page_setup.spine.x
    const frontXDifference = newDimensions.front.x - cover.page_setup.front.x

    cover.content = cover.content.map((element: TAmazonElement) => {
      const el: TAmazonElement = { ...element }

      if (
        ['back-image', 'front-image', 'back-background', 'front-background', 'spine-background'].includes(el.id) &&
        el.group in newDimensions
      ) {
        const group: TAmazonCoverSection = newDimensions[el.group as 'spine' | 'front' | 'back']

        el.left = group.x_background ?? el.left
        el.top = group.y_background ?? el.top
        el.width = group.width_background ?? el.width
        el.height = group.height_background ?? el.height

        return el
      }

      if (el.id?.startsWith('front')) {
        // front elements, modify left and top position
        el.left += frontXDifference
        el.top += yDifference
        return el
      }

      if (el.id?.startsWith('spine')) {
        // spine elements, modify left and top position
        el.left += spineXDifference
        el.top += yDifference
        return el
      }

      if (['custom-text', 'custom-image', 'custom-shape', 'image'].includes(el.type)) {
        if (el.left >= cover.page_setup.front.x) {
          // custom elements on front area
          el.left += frontXDifference
        } else if (el.left >= cover.page_setup.spine.x) {
          // custom elements on front area
          el.left += spineXDifference
        } else {
          // custom elements on back area
          el.left += backXDifference
        }

        el.top += yDifference
      }

      return el
    })

    cover.page_setup = newDimensions
    await handleSpineTitleAndAuthorAfterPageCountChange(newPageCount)
    cover.pages = newPageCount
    cover.width = Math.round(newDimensions.full.width)
    cover.height = Math.round(newDimensions.full.height)

    await save(cover)
  }

  async function removeMany(ids: number[]): Promise<boolean> {
    try {
      ids.forEach((id) => {
        const idx = idsWithoutThumbnails.value.indexOf(id)
        if (idx >= 0) {
          idsWithoutThumbnails.value.splice(idx, 1)
        }
      })

      const res = await http.post<{ result: boolean }>('/amazon-covers/delete-many', { ids })
      res.result && fetch()
      return res.result || false
    } catch {
      console.log('Failed to remove covers')
    }
    return false
  }

  async function create() {
    if (upgrade && !users?.isProOrHigherAccount()) {
      upgrade.showUpgradeModal('amazon-covers', Products.Pro)
      return
    }
    await modal.open<TProject | undefined>(() => import('@/components/modal/DesModalCreateCover.vue'))
  }
  async function rename(id: number, title: string) {
    try {
      await http.post('/amazon-covers/rename', { id, title }, true)
      return true
    } catch {
      return false
    }
  }

  async function updateAmazonCoverIdInThumbnailInfo(id: number, thumbnailInfoId: number): Promise<boolean> {
    try {
      return await http.post('/amazon-covers/update-amazon-cover-id-in-thumbnail-info', { id, thumbnailInfoId })
    } catch {
      return false
    }
  }

  async function generateBackgroundThumbnails(id: number): Promise<TAmazonCoverThumbnailInfo | null> {
    try {
      const result: TAmazonCoverThumbnailInfo = await http.post('/amazon-covers/generate-background-thumbnails', { id })
      return result ?? null
    } catch {
      return null
    }
  }

  async function getDimensions(form: TAmazonCoverDimensionsForm): Promise<TAmazonPageSetup | null> {
    try {
      const result = await http.post<TAmazonPageSetup>('/amazon-covers/dimensions', form)
      return result ?? null
    } catch {
      return null
    }
  }

  async function getTemplateFonts(templateId: number): Promise<{ fonts: string[]; customFontLinks: string[] }> {
    try {
      const res = await http.get<{ status: string; fonts: string[]; custom_font_links: string[] }>(
        `/templates/${templateId}/fonts`,
      )
      if (res.status !== 'success') throw new Error('Failed to fetch template fonts')
      return { fonts: res.fonts, customFontLinks: res.custom_font_links }
    } catch {
      return { fonts: [], customFontLinks: [] }
    }
  }

  function isProjectChanged(): null | string {
    if (!cover.value || !cover.value.project || cover.value.binding === 'kindle') return null

    const project = cover.value.project
    const setup = { ...cover.value.page_setup.front }
    const factor = 0.265

    // based on Builder\AmazonCovers\AmazonCoversController::dimensions
    if (cover.value.binding === 'hardcover') {
      setup.width -= 5
      setup.height -= 6
    }

    if (project.pages_count !== cover.value.pages) {
      console.log('Number of pages has changed:', project.pages_count, cover.value.pages)
      return `The page count has changed from ${cover.value.pages} to ${project.pages_count}`
    }

    if (!project.dimensions) return null

    if (Math.abs(project.dimensions.width * factor - setup.width) > 1) {
      console.log('Width of ebook has changed:', project.dimensions.width * factor, setup.width)
      return 'The project dimensions have been updated'
    }

    if (Math.abs(project.dimensions.height * factor - setup.height) > 1) {
      console.log('Height of ebook has changed:', project.dimensions.height * factor, setup.height)
      return `The project dimensions have been updated`
    }

    return null
  }

  async function exportCover(project: TAmazonCover): Promise<Blob | null> {
    try {
      const data = await http.get<{ status: 'success'; url: string } | { status: 'error'; response: string }>(
        `/amazon-covers/print/${project.id}/${project.binding === 'kindle' ? 100 : 96}`,
      )

      if (data.status !== 'success') {
        throw new Error(`File URL get failed: ${data.response}`)
      }

      const fileRes = await globalThis.fetch(data.url)
      if (!fileRes.ok) {
        throw new Error(`File fetch failed: ${fileRes.status}`)
      }

      track('cover-download', { cover_id: project.id })
      return await fileRes.blob()
    } catch (err) {
      console.error(err)
      return null
    }
  }

  function resetForNewCover(): void {
    cover.value = null
  }

  function getFittingAmazonCoverDimension(): TAmazonCoverSize {
    const trimSize = cover.value?.page_setup?.initial_trim_size
    const pageSetupFront = cover.value?.page_setup?.front

    if (trimSize?.width && trimSize?.height) {
      return findNearestAmazonCoverSize(trimSize.width, trimSize.height)
    } else if (pageSetupFront?.width && pageSetupFront?.height) {
      return findNearestAmazonCoverSize(pageSetupFront.width, pageSetupFront.height)
    }

    // fallback, return 6" x 9"
    return findNearestAmazonCoverSize(152.4, 228.6)
  }

  return {
    covers,
    cover,
    total,
    fetch,
    ensureCovers,
    invalidate,
    fetchNext,
    fetchBackgroundThumbnailInfo,
    generateThumbnailById,
    getFittingAmazonCoverDimension,
    get,
    getDimensions,
    getTemplateFonts,
    generateBackgroundThumbnails,
    updateAmazonCoverIdInThumbnailInfo,
    minPageAmountForHardcover,
    minPageAmountForPaperback,
    adjustCoverToTheNewPageCount,
    save,
    remove,
    removeMany,
    create,
    rename,
    bindingTypeOption,
    interiorTypeOptions,
    paperTypeOptions,
    combinedPaperTypeOptions,
    generateThumbnail,
    generateCoverKeywords,
    idsWithoutThumbnails,
    images,
    sort,
    changeCoverTemplate,
    isProjectChanged,
    exportCover,
    resetForNewCover,
    barcodeZIndex,
  }
})
